import { createContext, useContext } from 'react'
import type { RecurringMode } from '../types/recurringChecklist'

export type { RecurringMode }

/**
 * When present, the entire ChecklistDrawingBoard subtree treats the mounted
 * canvas as a paginated recurring surface (Daily / Weekly) rather than a
 * one-shot element-checklist:
 *
 *   - Tick reads use `item.occurrences[focusedKey]?.isCompleted` instead of
 *     the mirrored top-level `isCompleted`.
 *   - Tick writes route through `setOccurrenceCompletion(id, focusedKey, …)`
 *     so each occurrence tracks its own completion.
 *   - Every other write (title, weight, position, member, attachments) still
 *     writes to the master item — the singleton model's whole point.
 *
 * Consumers should call `useRecurringLens()` and check for null: null means
 * "we're on Main canvas or another element-scoped surface, behave as before."
 */
export type RecurringLens = {
  workspaceId: string
  mode: RecurringMode
  focusedKey: string
  /** ISO date at midnight for the focused occurrence — used by chip
   *  labels and calendar shelf integrations that want a real Date. */
  focusedDateIso: string
  /** Rolling window (today + next N-1 days/weeks) that recurring items
   *  materialize into. Passed through so the ChecklistDrawingBoard can
   *  seed newly-authored items with the same set of occurrence keys that
   *  materializeRecurringWindow would fill on the next boot — otherwise
   *  a freshly-created item lands with an empty occurrences map and never
   *  passes the "does this occurrence exist for the focused day" filter
   *  until the next bootstrap. */
  recurringWindowDays: number
  /** Optional jump callback so the RecurringOccurrenceChip embedded on a
   *  paginated card can navigate the whole canvas to another occurrence.
   *  Absent lens (Main canvas) OR absent callback → chip drilldown is
   *  read-only history preview with no navigation. */
  onFocusedDateChange?: (date: Date) => void
}

export const RecurringLensContext = createContext<RecurringLens | null>(null)

export const useRecurringLens = (): RecurringLens | null => useContext(RecurringLensContext)

/** Synthetic parent-container id used by recurring items and the synthetic
 *  TimelineElement mounted for the Daily / Weekly shells. Keeping the
 *  workspaceId in the string means it survives round-tripping through the
 *  existing checklist-item queries that filter by parentContainerId. */
export const recurringContainerId = (workspaceId: string, mode: RecurringMode) =>
  `recurring:${workspaceId}:${mode}`

/** Extract mode from a recurringContainerId. Returns null if not one. */
export const parseRecurringContainerId = (
  containerId: string,
): { workspaceId: string; mode: RecurringMode } | null => {
  const parts = containerId.split(':')
  if (parts.length !== 3 || parts[0] !== 'recurring') return null
  const mode = parts[2]
  if (mode !== 'DAILY' && mode !== 'WEEKLY') return null
  return { workspaceId: parts[1], mode }
}
